import { useCallback, useRef, useEffect } from 'react';

interface DescriptionEditorProps {
  value: string;
  onChange: (html: string) => void;
}

export default function DescriptionEditor({ value, onChange }: DescriptionEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const toolbarButtons = [
    { command: 'bold', icon: 'B', title: 'Bold' },
    { command: 'italic', icon: 'I', title: 'Italic' },
    { command: 'underline', icon: 'U', title: 'Underline' },
    { command: 'strikeThrough', icon: 'S', title: 'Strikethrough' },
    { divider: true },
    { command: 'insertUnorderedList', icon: '•', title: 'Bullet List' },
    { command: 'insertOrderedList', icon: '1.', title: 'Numbered List' },
    { divider: true },
    { command: 'justifyLeft', icon: '≡', title: 'Align Left' },
    { command: 'justifyCenter', icon: '≡', title: 'Align Center' },
    { command: 'justifyRight', icon: '≡', title: 'Align Right' },
    { divider: true },
    { command: 'createLink', icon: '🔗', title: 'Insert Link', prompt: true },
    { command: 'removeFormat', icon: '✕', title: 'Clear Formatting' },
  ];

  const handleButtonClick = useCallback(
    (btn: (typeof toolbarButtons)[0]) => {
      if ('divider' in btn && btn.divider) return;
      if ('command' in btn) {
        if (btn.prompt) {
          const url = prompt('Enter URL:');
          if (url) execCommand(btn.command, url);
        } else {
          execCommand(btn.command);
        }
      }
    },
    [execCommand],
  );

  return (
    <div className="rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-2">
        {toolbarButtons.map((btn, i) =>
          'divider' in btn && btn.divider ? (
            <div key={i} className="mx-1 h-5 w-px bg-border" />
          ) : (
            <button
              key={i}
              type="button"
              title={btn.title}
              onClick={() => handleButtonClick(btn)}
              className="flex h-8 w-8 items-center justify-center rounded text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {btn.icon}
            </button>
          ),
        )}
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[200px] p-4 text-sm leading-relaxed focus:outline-none"
        suppressContentEditableWarning
      />
    </div>
  );
}
